import { fromEvent, Observable, Subject } from 'rxjs';
import EventEmitter from 'eventemitter3';
import { distinctUntilChanged, filter, ignoreElements, map, shareReplay } from 'rxjs/operators';
import { ServiceContainer } from '@tv2-player/service-container/src/serviceContainer';
import UnifiedPlayer from '../core/UnifiedPlayer';
import CORE_EVENTS from '../core/events';
import { ObservableUnifiedPlayer } from '../types/ObservableUnifiedPlayer';
import { trackPluginCall } from './overseer/overseer';
import { VcrCollector } from './VcrCollector';
import { PollResult } from '../modules/dataProviders/types';
import { UseTrackingMappedMedia } from '../plugins/Tracking';
import type { Media } from '../types';

/**
 * This file contains a VCR implementation for the UnifiedPlayer.
 * It records all events that are emitted by the player and can replay them.
 * It also records the guid, media and epg data that is passed to the player.
 * It is useful for debugging and reproducing bugs.
 *
 * How to enable:
 * For chromecast use the test sender and enable the record feature
 * For web append a ?vcr=true to the url
 * For snowpiercer set the environment variable ENABLE_VCR=true - so far this can only be done on a computer and not on the tv
 */

type PlayerEvent = { origin: 'player'; eventName: string; args: any[] };

type VCREvent =
  | { origin: 'tracking'; value: any }
  | { origin: 'media'; value: any }
  | { origin: 'epg'; value: any }
  | PlayerEvent;

export type TimestampedVCREvent = VCREvent & { timestamp: number };

let events: TimestampedVCREvent[] = [];
let disposables: any[] = [];

export function resetDefaultVcrCollector() {
  events = [];
}

export function defaultVcrCollector(e: TimestampedVCREvent) {
  events.push(e);
}

/**
 * Starts a recording of the player. It can later be downloaded using the vcr.download() function on window
 * @param player - the player to record
 * @param media$ - an observable that emits the media
 * @param epg$ - an observable that emits the epg
 * @param tracking$ - an observable that emits tracking information
 */
function recordPlayer(
  player: UnifiedPlayer,
  media$: Observable<any>,
  epg$: Observable<PollResult>,
  tracking$: Observable<PollResult['liveTrackingResult']>
) {
  const sessionId = Date.now().toString();
  resetDefaultVcrCollector();
  const localDisposables: any[] = [];
  disposables = localDisposables;

  const collectors = ServiceContainer.get(VcrCollector);

  function addEvent(e: VCREvent) {
    const timestamped = { ...e, timestamp: Date.now() };

    collectors.forEach(c => c.collect(timestamped, sessionId));

    trackPluginCall('vcr', 'event', e);
  }

  // We subscribe to all events that the player emits and record them
  Object.keys(CORE_EVENTS).forEach(namespace => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    CORE_EVENTS[namespace].forEach(event => {
      const eventName = `${namespace}.${event}`;
      const handler = (...args: any[]) => {
        addEvent({
          origin: 'player',
          eventName,
          args: JSON.parse(serialize(args)),
        });
      };
      player.on(eventName, handler);

      // We track the subscription so we can unsubscribe later
      const unsubscribeFn = () => player.off(eventName, handler);
      localDisposables.push(unsubscribeFn);
    });
  });

  const mediaSubscription = media$.subscribe({
    next: (media: Media) => {
      addEvent({
        origin: 'media',
        value: media,
      });
    },
    // eslint-disable-next-line no-console
    error: console.error,
  });
  localDisposables.push(() => mediaSubscription.unsubscribe());

  const epgSubscription = epg$.subscribe({
    next: (epg: any) => {
      addEvent({
        origin: 'epg',
        value: epg,
      });
    },
    // eslint-disable-next-line no-console
    error: console.error,
  });
  localDisposables.push(() => epgSubscription.unsubscribe());

  const trackingSubscription = tracking$.subscribe({
    next: (tracking: any) => {
      addEvent({
        origin: 'tracking',
        value: tracking,
      });
    },
    // eslint-disable-next-line no-console
    error: console.error,
  });
  localDisposables.push(() => trackingSubscription.unsubscribe());
}

/** Creates a JSON file and triggers a download */
export function vcrDownload(messages: any) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(
    new Blob([JSON.stringify(messages, undefined, 2)], { type: 'application/json' })
  );
  a.download = 'vcr.json';
  a.click();
}

export function record(
  player$: Observable<UnifiedPlayer>,
  media$: Observable<any>,
  epg$: Observable<PollResult>,
  tracking$: Observable<PollResult['liveTrackingResult']>
) {
  function disposeObservables() {
    disposables.forEach(disposer => {
      try {
        disposer();
        // eslint-disable-next-line no-empty
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    });
  }

  const subscription = player$.subscribe(player => {
    disposeObservables();
    recordPlayer(player, media$, epg$, tracking$);
  });

  (globalThis as any).vcr = {
    download: () => vcrDownload(events),
    getJson: () => JSON.stringify(events, undefined, 2),
  };

  return {
    destroy: () => {
      subscription.unsubscribe();
      disposeObservables();
    },
  };
}

let lastReplayedEvent: TimestampedVCREvent | null = null;
export function getLastReplayedEvent() {
  return lastReplayedEvent;
}

export async function replay(
  player: VcrPlayer,
  media$: Subject<any>,
  tracking$: Subject<any>,
  epg$: Subject<any>,
  input: any[]
) {
  const parsedEvents = input as TimestampedVCREvent[];
  parsedEvents.forEach(event => {
    lastReplayedEvent = event;
    if (event.origin === 'tracking') {
      tracking$.next(event.value);
    }
    if (event.origin === 'media') {
      media$.next(event.value);
    }
    if (event.origin === 'epg') {
      // If 'epg' contains tracking, push it to tracking$
      if (event.value.tracking) {
        tracking$.next(event.value);
      }
      epg$.next(event.value);
    }
    if (event.origin === 'player') {
      player.playEvent(event.eventName, event.args);
    }
  });
}

interface ReplayContext {
  player: ObservableUnifiedPlayer;
  media$: Observable<UseTrackingMappedMedia>;
  epg$: Observable<any>;
  tracking$: Observable<any>;
}

type Destroyable = {
  destroy: () => void;
};

export function replay2(input: any[], thingFn: (ctx: ReplayContext) => Destroyable) {
  const media$ = new Subject<UseTrackingMappedMedia>();

  const mediaShared$ = media$.pipe(shareReplay({ bufferSize: 1, refCount: true }));

  const player = new VcrPlayer();
  const epg$ = new Subject<any>();
  const tracking$ = new Subject<any>();

  const instance = thingFn({
    player,
    media$: mediaShared$,
    epg$,
    tracking$,
  });

  let destroyed = false;
  const parsedEvents = input as TimestampedVCREvent[];
  parsedEvents.forEach((event, index) => {
    if (destroyed) {
      return;
    }
    lastReplayedEvent = event;
    if (event.origin === 'media') {
      media$.next(event.value);
    }
    if (event.origin === 'epg') {
      epg$.next(event.value);
      if (event.value.tracking) {
        tracking$.next(event.value);
      }
    }
    if (event.origin === 'tracking') {
      tracking$.next(event.value);
    }
    if (event.origin === 'player') {
      if (event.eventName === 'destroy') {
        media$.complete();
        player.playEvent(event.eventName, event.args);
        instance.destroy();

        replay2(input.slice(index + 1), thingFn);
        destroyed = true;
      } else {
        player.playEvent(event.eventName, event.args);
      }
    }
  });
}

export class VcrPlayer extends EventEmitter implements ObservableUnifiedPlayer {
  constructor() {
    super();
    this.#update$ = new Subject<any>();
  }

  playEvent(eventName: string, args: any[]) {
    if (args[0].currentMediaTime) {
      this.currentTime = args[0].currentMediaTime;
    }
    if (args[0].currentTime) {
      this.currentTime = args[0].currentTime;
    }
    if (eventName === 'player.destroy') {
      this.#update$.complete();
      this.#update$ = new Subject<any>();
    }
    this.emit(eventName, ...args);
  }

  #update$: Subject<any>;

  currentTime = 0;
  observe<T>(eventOrFunction?: string | ((x: any) => any)): Observable<T> {
    switch (typeof eventOrFunction) {
      case 'string':
        return fromEvent(this, eventOrFunction) as Observable<T>;
      case 'function':
        return this.#update$.pipe(
          map(eventOrFunction),
          filter(x => x !== undefined),
          distinctUntilChanged()
        );
      default:
        return this.#update$.pipe(ignoreElements());
    }
  }

  ended = false;

  duration = 0;

  paused = false;

  currentBitrate = 0;

  droppedFrames = 0;

  subtitlesEnabled = false;

  subtitlesAvailable = false;
}

// Serialize as JSON but remove circular references
function serialize(obj: any) {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    return value;
  });
}
